/**
 * Options resolver for `select` / `multi_select` catalog fields.
 *
 *
 * Background. The catalog (`@inside-us-aos/domain`) ships enum *keys*
 * for every select / multi_select field via the per-PDF
 * `widgetValueProjection` map but does NOT carry a generic
 * `options[].label` array on `FieldDefinition`. ADR 0006 declares
 * `widgetValueProjection` PDF-only; this resolver treats it
 * exclusively as an enum-key source (no widget basenames, no PDF
 * intent leak into the UI dispatch).
 *
 * Resolution order for a given field:
 *
 *   1. Per-field override map (`OPTION_LABEL_OVERRIDES`) — curated
 *      human-friendly labels for keys whose title-case fallback is
 *      not good enough.
 *   2. Title-case fallback — the enum key, converted from
 *      `snake_case`/`-`-separated keys into Title Case (`asylee` →
 *      `Asylee`, `family-immediate` → `Family Immediate`,
 *      `5_pct_owner` → `5 Pct Owner`).
 *
 * The resolver also emits a discovery list (`getDiscoveredFallbacks`)
 * so Phase 7d's curator-facing audit can see which keys still rely on
 * the title-case fallback rather than a curated label.
 */
import type { FieldDefinition, FieldId } from '@inside-us-aos/domain';

export interface IResolvedOption {
  value: string;
  label: string;
}

/**
 * Shared enum lists for fields that share the same domain across
 * multiple catalog entries. Inlining 50+250 entries per field would
 * balloon the override map; sharing keeps the source of truth small
 * and consistent.
 *
 * Values match the verbatim USCIS PDF widget keys (validated against
 * docs/rebuild/i-485-* and i-130-* PDF widget dumps). Labels are the
 * user-facing display strings.
 *
 * Phase 7d-1.
 */

/** USCIS unit-type for street addresses. PDF accepts "Apt", "Ste", "Flr". */
const UNIT_TYPES: Readonly<Record<string, string>> = {
  Apt: 'Apartment (Apt)',
  Ste: 'Suite (Ste)',
  Flr: 'Floor (Flr)',
};

/**
 * U.S. states + DC + territories. Two-letter postal codes are the
 * verbatim values USCIS PDF widgets accept. Territories included
 * cover the standard USCIS state-select set: AS (American Samoa),
 * GU (Guam), MP (Northern Mariana Islands), PR (Puerto Rico),
 * VI (U.S. Virgin Islands). COFA states (FM, MH, PW) are accepted
 * on a handful of newer forms but not the I-485/I-130/I-864 set;
 * they can be added per-field if needed.
 */
const US_STATES: Readonly<Record<string, string>> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  AS: 'American Samoa',
  GU: 'Guam',
  MP: 'Northern Mariana Islands',
  PR: 'Puerto Rico',
  VI: 'U.S. Virgin Islands',
};

/**
 * Sex / gender options. USCIS forms recognize "M" / "F" on most
 * forms; newer (post-2024) editions accept "X" for some fields.
 * Catalog uses lowercased keys.
 */
// USCIS forms only accept Male / Female on the sex checkbox set
// (binary). No nonbinary widget exists on any AOS-relevant PDF.
const SEX_OPTIONS: Readonly<Record<string, string>> = {
  male: 'Male',
  female: 'Female',
};

/**
 * Applicant current immigration status.
 *
 * Mirrors the screener-onboarding question ("How did you most
 * recently enter or stay in the U.S.?", see
 * `screener/screenerConfig.ts:beneficiary_immigration_status`).
 * Replaces a previously free-text catalog field.
 *
 * The KEYS are intentionally the USCIS-friendly strings the I-485
 * Pt1Line27 / I-765 Pt2Line24 text widgets expect verbatim — the
 * PDF engine writes the select value directly to the text widget
 * with no enum→text projection step. The VALUES are the longer,
 * applicant-friendly descriptions shown in the dropdown.
 *
 * Keep this map in sync with:
 *   - screenerConfig.ts (option `value`s)
 *   - screenerHandoff.ts (the allowlist + projection logic)
 */
const IMMIGRATION_STATUS_OPTIONS: Readonly<Record<string, string>> = {
  'Nonimmigrant visa entry':
    'I entered lawfully on a visa (e.g., F-1, H-1B, B-1/B-2, K-1)',
  'Visa Waiver Program (ESTA)':
    'I entered through the Visa Waiver Program (ESTA)',
  'Paroled into the U.S.': 'I was paroled into the U.S.',
  'Asylee/Refugee': 'I hold asylee or refugee status',
  'Temporary Protected Status (TPS)':
    'I hold Temporary Protected Status (TPS)',
  'Entered without inspection': 'I entered without inspection',
  Other: 'Something else',
};

/**
 * Beneficiary I-94 class of admission.
 *
 * Mirrors the legacy app's `I94_STATUS_DESCRIPTIONS` map (visa code
 * → human description; see `docs/rebuild/legacy-comparison-2026-05-
 * 09.md` §B3) but consolidated to one entry per description (the
 * legacy map had multiple visa-code keys for the same description,
 * e.g. B-1/B-2/B-1/B-2 → "Visitor"). Replaces the previously free-
 * text `beneficiary.i94.status_at_arrival` catalog field.
 *
 * Per ADR 0017's text-widget pattern, the KEYS are the USCIS-
 * friendly class-of-admission strings the I-485 Pt1Line11_admitted_as
 * (and the I-765 / I-131 equivalents) text widgets expect verbatim
 * — the PDF engine writes the selected key directly with no
 * widgetValueProjection step. The LABELS append the visa codes so
 * applicants who think in codes can still find the row.
 *
 * Keep in sync with `docs/rebuild/legacy-comparison-2026-05-09.md`
 * if the catalog adds/removes status entries.
 */
const I94_CLASS_OF_ADMISSION_OPTIONS: Readonly<Record<string, string>> = {
  // Visitor categories.
  Visitor: 'Visitor (B-1, B-2, or B-1/B-2)',
  'Business Visitor': 'Business visitor (B-1)',
  // Students.
  Student: 'Student (F-1)',
  'Vocational Student': 'Vocational student (M-1)',
  // Exchange visitors.
  'Exchange Visitor': 'Exchange visitor (J-1)',
  'Exchange Visitor Dependent': 'Dependent of exchange visitor (J-2)',
  // Workers.
  'Temporary Worker': 'Temporary worker (H-1B, H-2B)',
  'Agricultural Worker': 'Agricultural worker (H-2A)',
  'Dependent of Worker': 'Dependent of temporary worker (H-4)',
  // Intracompany transferees.
  'Intracompany Transferee': 'Intracompany transferee (L-1, L-1A, L-1B)',
  'Dependent of Transferee': 'Dependent of intracompany transferee (L-2)',
  // Other categories.
  'Extraordinary Ability': 'Person of extraordinary ability (O-1)',
  'Athlete/Entertainer': 'Athlete or entertainer (P-1)',
  'Fiancé(e)': 'Fiancé(e) of U.S. citizen (K-1)',
  'Spouse of U.S. Citizen': 'Spouse of U.S. citizen (K-3)',
  // Parole categories.
  Paroled: 'Paroled into the U.S.',
  'Humanitarian Parole': 'Humanitarian parole (HP)',
  'Advance Humanitarian Parole': 'Advance humanitarian parole (AHP)',
  // DACA / TPS / EAD.
  'Temporary Protected Status': 'Temporary Protected Status (TPS)',
  'Employment Authorized': 'Employment authorization (EAD)',
  // Catch-all.
  Other: 'Something else (specify below)',
};

/**
 * ISO 3166-1 alpha-2 country codes accepted by USCIS forms.
 * Stored as a space-separated string then split at module load
 * to keep the source short and easy to maintain.
 *
 * Country labels are generated at runtime via `Intl.DisplayNames`
 * (Node 18+ / modern browsers) so we don't ship a hard-coded
 * country-name table that drifts from the international list.
 */
const COUNTRY_CODES = (
  'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ ' +
  'BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ ' +
  'CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ ' +
  'DE DJ DK DM DO DZ ' +
  'EC EE EG EH ER ES ET ' +
  'FI FJ FK FM FO FR ' +
  'GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY ' +
  'HK HM HN HR HT HU ' +
  'ID IE IL IM IN IO IQ IR IS IT ' +
  'JE JM JO JP ' +
  'KE KG KH KI KM KN KP KR KW KY KZ ' +
  'LA LB LC LI LK LR LS LT LU LV LY ' +
  'MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ ' +
  'NA NC NE NF NG NI NL NO NP NR NU NZ ' +
  'OM ' +
  'PA PE PF PG PH PK PL PM PN PR PS PT PW PY ' +
  'QA ' +
  'RE RO RS RU RW ' +
  'SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ ' +
  'TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ ' +
  'UA UG UM US UY UZ ' +
  'VA VC VE VG VI VN VU ' +
  'WF WS ' +
  'YE YT ' +
  'ZA ZM ZW'
).split(/\s+/);

/**
 * Lazily generated countries map: code → English region display name.
 *
 * Insertion order is alphabetical-by-display-name. JavaScript object
 * iteration preserves insertion order for string keys, and the
 * resolver renders dropdown options in that order. So a user sees
 * "Afghanistan" → "Albania" → "Algeria" rather than the
 * alphabetical-by-code order ("Andorra" → "United Arab Emirates" →
 * "Afghanistan") that confused testing.
 *
 * The 2-letter code stays as the underlying enum value (USCIS PDF
 * widgets accept the code, not the display name).
 */
const COUNTRIES: Readonly<Record<string, string>> = (() => {
  let display: Intl.DisplayNames | null = null;
  try {
    display = new Intl.DisplayNames(['en'], { type: 'region' });
  } catch {
    display = null;
  }
  // Build (code, label) pairs, then sort by label, then insert.
  const pairs: Array<readonly [string, string]> = [];
  for (const code of COUNTRY_CODES) {
    if (code.length === 0) continue;
    pairs.push([code, display?.of(code) ?? code]);
  }
  pairs.sort((a, b) => a[1].localeCompare(b[1], 'en'));
  const out: Record<string, string> = {};
  for (const [code, label] of pairs) {
    out[code] = label;
  }
  return out;
})();

/**
 * Per-field human-readable label overrides. The outer key is the
 * catalog `FieldId`; the inner key is the enum value. Anything not
 * in this map falls back to a title-cased version of the enum key.
 *
 * Spot-curated for the highest-traffic enums (sex, basis-for-filing,
 * eligibility category, etc.). The full audit is a Phase 7d task —
 * `getDiscoveredFallbacks` surfaces unlabeled keys for the curator.
 */
/**
 * Employment-history per-row status options. Each row of the I-485
 * Pt4 Item 7 / I-130 Pt2 employment-history group is one of five
 * period types; the catalog gates which sibling fields apply (see
 * `docs/rebuild/spec-employment-history-status.md`).
 */
const EMPLOYMENT_HISTORY_STATUS_OPTIONS: Readonly<Record<string, string>> = {
  employed: 'Employed',
  self_employed: 'Self-employed',
  in_school: 'In school / Student',
  unemployed: 'Unemployed',
  retired: 'Retired',
};

/**
 * I-485 Pt1Line25 entry-type options. The USCIS PDF lists the four
 * mutually-exclusive checkboxes as sentence completions ("I was
 * inspected and admitted as…", "I was paroled as…", "I came without
 * admission or parole", "Other"). Mirroring that phrasing in the
 * dropdown keeps the option meaning self-contained rather than
 * relying on the field label to complete the sentence.
 */
const ENTRY_TYPE_OPTIONS: Readonly<Record<string, string>> = {
  inspected_admitted:
    'I was inspected at a Port of Entry and admitted (e.g., as a student, visitor, or worker)',
  inspected_paroled:
    'I was inspected at a Port of Entry and paroled (e.g., humanitarian parole, Cuban parole)',
  without_admission_or_parole:
    'I came into the United States without admission or parole',
  other: 'Other',
};

export const OPTION_LABEL_OVERRIDES: Readonly<
  Record<string, Readonly<Record<string, string>>>
> = {
  // Sex / gender — applicant, parents, sponsor, sponsor's parents.
  'beneficiary.demographics.sex': SEX_OPTIONS,
  'beneficiary.parent_one.demographics.sex': SEX_OPTIONS,
  'beneficiary.parent_two.demographics.sex': SEX_OPTIONS,
  'sponsor.demographics.sex': SEX_OPTIONS,
  'sponsor.parent_one.demographics.sex': SEX_OPTIONS,
  'sponsor.parent_two.demographics.sex': SEX_OPTIONS,

  // Address unit-type — every address bag shares the same Apt/Ste/Flr set.
  'beneficiary.address_history.unit_type': UNIT_TYPES,
  'beneficiary.current_physical_address.unit_type': UNIT_TYPES,
  'beneficiary.current_spouse.physical_address.unit_type': UNIT_TYPES,
  'beneficiary.intended_address.unit_type': UNIT_TYPES,
  'beneficiary.foreign_address.unit_type': UNIT_TYPES,
  'beneficiary.most_recent_foreign_address.unit_type': UNIT_TYPES,
  'beneficiary.employment_history.address.unit_type': UNIT_TYPES,
  'beneficiary.mailing_address.unit_type': UNIT_TYPES,
  'sponsor.address_history.unit_type': UNIT_TYPES,
  'sponsor.current_physical_address.unit_type': UNIT_TYPES,
  'sponsor.employment_history.address.unit_type': UNIT_TYPES,
  'sponsor.mailing_address.unit_type': UNIT_TYPES,

  // U.S. state — every U.S. address bag shares the same 50+DC+territories set.
  'beneficiary.address_history.state': US_STATES,
  'beneficiary.current_physical_address.state': US_STATES,
  'beneficiary.current_spouse.physical_address.state': US_STATES,
  'beneficiary.employment_history.address.state': US_STATES,
  'beneficiary.intended_address.state': US_STATES,
  'beneficiary.last_arrival.place.state': US_STATES,
  'beneficiary.mailing_address.state': US_STATES,
  'beneficiary.proceedings.place.state': US_STATES,
  'sponsor.address_history.state': US_STATES,
  'sponsor.current_physical_address.state': US_STATES,
  'sponsor.employment_history.address.state': US_STATES,
  'sponsor.mailing_address.state': US_STATES,
  'sponsor.prior_i130_filings.place_of_filing.state': US_STATES,
  // I-130 Pt2Line40e (LPR place of admission state) added in AOS gap-fill.
  'application.petitioner.lpr.place.state': US_STATES,

  // I-485 Pt1Line25 entry-type — four mutually-exclusive sentence
  // completions for "When did you last arrive in the United States?".
  'beneficiary.last_arrival.entry_type': ENTRY_TYPE_OPTIONS,

  // Employment-history per-row status — drives which sibling fields
  // apply to each period (employer / school / unemployment / etc.).
  'beneficiary.employment_history.status':
    EMPLOYMENT_HISTORY_STATUS_OPTIONS,
  'sponsor.employment_history.status': EMPLOYMENT_HISTORY_STATUS_OPTIONS,

  // Country — every address / birth-place / origin field shares the
  // same ISO 3166-1 list (labels generated via Intl.DisplayNames at
  // runtime, see COUNTRIES helper above).
  'beneficiary.address_history.country': COUNTRIES,
  'beneficiary.birth.place.country': COUNTRIES,
  'beneficiary.children.birth.country': COUNTRIES,
  'beneficiary.citizenship.primary_country': COUNTRIES,
  'beneficiary.citizenship.secondary_country': COUNTRIES,
  'beneficiary.current_physical_address.country': COUNTRIES,
  'beneficiary.current_spouse.birth.place.country': COUNTRIES,
  'beneficiary.current_spouse.marriage.place.country': COUNTRIES,
  'beneficiary.current_spouse.physical_address.country': COUNTRIES,
  'beneficiary.most_recent_foreign_address.country': COUNTRIES,
  'beneficiary.employment_history.address.country': COUNTRIES,
  'beneficiary.parent_one.birth.place.country': COUNTRIES,
  'beneficiary.parent_one.current_residence.country': COUNTRIES,
  'beneficiary.parent_two.birth.place.country': COUNTRIES,
  'beneficiary.parent_two.current_residence.country': COUNTRIES,
  'beneficiary.prior_immigrant_visa.location.country': COUNTRIES,
  'beneficiary.prior_marriages.place_ended.country': COUNTRIES,
  'beneficiary.prior_marriages.place_started.country': COUNTRIES,
  'beneficiary.travel_document.issuing_country': COUNTRIES,
  'eligibility.organization_memberships.country': COUNTRIES,
  'sponsor.address_history.country': COUNTRIES,
  'sponsor.birth.place.country': COUNTRIES,
  'sponsor.current_physical_address.country': COUNTRIES,
  'sponsor.current_spouse.marriage.place.country': COUNTRIES,
  'sponsor.domicile.country': COUNTRIES,
  'sponsor.employment_history.address.country': COUNTRIES,
  'sponsor.mailing_address.country': COUNTRIES,
  'sponsor.parent_one.birth.place.country': COUNTRIES,
  'sponsor.parent_one.current_residence.country': COUNTRIES,
  'sponsor.parent_two.birth.place.country': COUNTRIES,
  'sponsor.parent_two.current_residence.country': COUNTRIES,

  // Beneficiary current immigration status — I-485 Pt1Line27 +
  // I-765 Pt2Line24. See IMMIGRATION_STATUS_OPTIONS docstring above.
  'beneficiary.immigration_status.current': IMMIGRATION_STATUS_OPTIONS,

  // Beneficiary I-94 class of admission — I-485 Pt1Line26c +
  // I-765 Pt2Line23 + I-131 Pt2Line12. See
  // I94_CLASS_OF_ADMISSION_OPTIONS docstring above. Mirrors the
  // legacy app's `I94_STATUS_DESCRIPTIONS` map (visa code → human
  // description) consolidated to one entry per description.
  'beneficiary.i94.status_at_arrival': I94_CLASS_OF_ADMISSION_OPTIONS,

  // Sponsor immigration status — I-864 Pt 1, I-130 Pt 2 item 36 (latter
  // omits us_national; form-engine constrains per-form).
  'sponsor.citizenship.status': {
    us_citizen: 'U.S. citizen',
    us_national: 'U.S. national',
    lawful_permanent_resident: 'Lawful permanent resident',
  },

  // I-485 Pt 5 item 2 — current spouse military service. Three-value
  // tri-state per the catalog helpText.
  'beneficiary.marital.current_spouse_is_military': {
    yes: 'Yes',
    no: 'No',
    not_applicable: 'Not applicable (not currently married)',
  },

  // I-131 Part 7 item 4 — number of trips the document is intended for.
  'application.travel_document.trip.intended_use': {
    one_trip: 'One trip',
    more_than_one_trip: 'More than one trip',
  },

  // I-485 Pt 9 Item 58 — annual household income bracket. Keys MUST
  // match the catalog's widgetValueProjection so the PDF engine can
  // resolve the right Pt9Line53_CB[N] box.
  'eligibility.public_charge.annual_household_income_bracket': {
    '0_to_27000': '$0 – $27,000',
    '27001_to_52000': '$27,001 – $52,000',
    '52001_to_85000': '$52,001 – $85,000',
    '85001_to_141000': '$85,001 – $141,000',
    over_141000: 'Over $141,000',
  },

  // I-485 Pt 9 Item 59 — household assets bracket → Pt9Line59_CB.
  'eligibility.public_charge.household_assets_bracket': {
    '0_to_18400': '$0 – $18,400',
    '18401_to_136000': '$18,401 – $136,000',
    '136001_to_321400': '$136,001 – $321,400',
    '321401_to_707100': '$321,401 – $707,100',
    over_707100: 'Over $707,100',
  },

  // I-485 Pt 9 Item 60 — household liabilities bracket → Pt9Line60_CB.
  'eligibility.public_charge.household_liabilities_bracket': {
    zero: '$0',
    '1_to_10100': '$1 – $10,100',
    '10101_to_57700': '$10,101 – $57,700',
    '57701_to_186800': '$57,701 – $186,800',
    over_186800: 'Over $186,800',
  },

  // I-485 Pt 9 Item 61 — highest education degree completed → Pt9Line61_CB.
  'eligibility.public_charge.highest_education_degree': {
    less_than_high_school_diploma: 'Less than a high school diploma',
    high_school_diploma_or_ged: 'High school diploma, GED, or alternative credential',
    one_or_more_years_college_no_degree: '1 or more years of college, no degree',
    associates_degree: "Associate's degree",
    bachelors_degree: "Bachelor's degree",
    masters_degree: "Master's degree",
    professional_degree: 'Professional degree (JD, MD, DMD, etc.)',
    doctorate_degree: 'Doctorate degree',
  },

  // Blanket question hiding I-485 Pt 9 Items 63 + 64 behind one
  // 4-way select. Drives derivation of those booleans + the row
  // matrices' visibility. injectSystemAnswers writes the derived
  // booleans before the PDF engine fills the form.
  'eligibility.public_charge.benefits_history_summary': {
    none: 'No, I have never received either',
    cash_only: 'Yes — government cash assistance only (SSI, TANF, etc.)',
    institutional_only: 'Yes — long-term institutional care only',
    both: 'Yes — both cash assistance and institutional care',
  },

  // I-485 Pt 9 Item 56 — public-charge exemption basis (one of 25
  // options). Most marriage-based filers select `not_exempt_complete_57_67`
  // which is auto-defaulted via injectSystemAnswers.
  'eligibility.public_charge.exemption_basis': {
    not_exempt_complete_57_67:
      'Not exempt — I will answer the public-charge questions below',
    afghan_iraqi_i360_ds157: 'Certain Afghan or Iraqi national (I-360 or DS-157)',
    lautenberg_parolee_general: 'Lautenberg Parolee',
    u_nonimmigrant_245m: 'U nonimmigrant under INA 245(m) (I-918 / I-918A / I-929)',
    asylee_i589_i730: 'Asylee (I-589 or I-730)',
    u_status_other_245m:
      'Other than INA 245(m), but in valid U nonimmigrant status when filing',
    vawa_self_petitioner: 'VAWA self-petitioner (I-360)',
    special_immigrant_juvenile: 'Special Immigrant Juvenile (I-360)',
    cuban_adjustment_act: 'Cuban Adjustment Act',
    hrifa_dependent_battered:
      'HRIFA dependent — battered spouse or child',
    t_nonimmigrant_245l: 'T nonimmigrant under INA 245(l) (I-914 / I-914A)',
    cuban_adjustment_battered: 'Cuban Adjustment Act — battered spouse or child',
    hrifa_dependent: 'HRIFA dependent',
    t_status_other_245l:
      'Other than INA 245(l), pending T status or valid T status when filing',
    registry_pre_1972: 'Continuous residence since before January 1, 1972 (Registry)',
    nacara: 'NACARA section 203',
    amerasian_homecoming: 'Amerasian Homecoming Act',
    polish_hungarian_parolee: 'Polish or Hungarian Parolee',
    cuban_haitian_irca: 'Cuban / Haitian Entrant (IRCA 1986 section 202)',
    lautenberg_parolee: 'A Lautenberg Parolee',
    foreign_operations_seah:
      'National of Vietnam, Cambodia, or Laos under Foreign Operations Act',
    american_indian_canada:
      'American Indian born in Canada (INA 289) or Texas Band Kickapoo',
    liberian_refugee_immigration_fairness:
      'Liberian Refugee Immigration Fairness (NDAA FY2020 §7611)',
    spouse_child_parent_us_citizen: 'Spouse, child, or parent of a U.S. citizen',
    syrian_national_pl_106_378: 'Syrian National (Public Law 106-378)',
  },

  // I-485 Pt 2 Line 2 — adjustment under INA section 245(i).
  // Three-value tri-state per the catalog helpText.
  'eligibility.category.section_245i': {
    yes: 'Yes',
    no: 'No',
    not_applicable: 'Not applicable',
  },

  'beneficiary.biographic.race': {
    white: 'White',
    asian: 'Asian',
    black_or_african_american: 'Black or African American',
    american_indian_or_alaska_native: 'American Indian or Alaska Native',
    native_hawaiian_or_other_pacific_islander:
      'Native Hawaiian or Other Pacific Islander',
  },
  // I-130 Item 37 — petitioner citizenship acquired via.
  'sponsor.citizenship.acquired_via': {
    birth_in_us: 'Birth in the United States',
    naturalization: 'Naturalization',
    parents: "Acquired through parents",
  },

  'sponsor.basis.for_filing': {
    petitioner: 'Petitioner (I-130 / I-129F filer)',
    i140_petitioner: 'I-140 petitioner',
    '5_pct_owner': '5% owner of the I-140 petitioner',
    joint_sponsor_only: 'Only joint sponsor',
    joint_sponsor_1of2: 'First of two joint sponsors',
    joint_sponsor_2of2: 'Second of two joint sponsors',
    substitute_sponsor: 'Substitute sponsor',
  },

  // 2026-05-26 — disambiguate the I-485 Pt2 Item 1 immigrant-category
  // dropdown. The catalog keys (family-immediate, family-preference,
  // fiance, etc.) title-case to nondescript labels ("Family Immediate"
  // vs "Family Preference") that don't tell the applicant which one
  // applies. The friendly labels below name the qualifying relationship
  // and the sponsor's status so a customer can pick correctly without
  // hunting through USCIS instructions.
  // 2026-05-26 — I-864 (Affidavit of Support) exemption basis. The
  // catalog enum keys auto-title-case to nondescript labels
  // ("Ssa 40 Quarters", "Widower I360") that confuse applicants. The
  // friendly labels below tell the user what each option actually
  // covers — and which one (`not_exempt_required`) is the marriage-
  // based default.
  'eligibility.affidavit_of_support.exemption_basis': {
    not_exempt_required:
      'Yes, I need to submit Form I-864 (typical marriage-based filer)',
    not_required:
      'No, I am not required to submit Form I-864 (rare — e.g., self-petitioner with sufficient personal income)',
    ssa_40_quarters:
      'I have earned 40 qualifying quarters of Social Security work credit',
    minor_us_citizen_parent:
      'I am under 18 and a U.S. citizen parent will automatically transmit citizenship to me',
    widower_i360:
      'I am the widow or widower of a U.S. citizen (filing under I-360)',
    vawa_self_petitioner:
      'I am a VAWA self-petitioner (abused spouse, child, or parent)',
  },

  'eligibility.category.selection': {
    'family-immediate':
      'Spouse, parent, or unmarried child under 21 of a U.S. citizen (immediate relative)',
    'family-preference':
      'Spouse or unmarried child of a permanent resident, or other family-based preference category',
    fiance: 'Fiancé(e) of a U.S. citizen (K-1 visa holder)',
    widower: 'Widow or widower of a U.S. citizen',
    vawa: 'VAWA self-petitioner (abused spouse, child, or parent)',
    'eb-entrepreneur': 'Employment-based — entrepreneur or investor (EB-5)',
    'eb-worker': 'Employment-based — worker (EB-1, EB-2, EB-3, etc.)',
    'si-religious-worker': 'Special immigrant — religious worker',
    'si-afghan-iraqi': 'Special immigrant — Afghan or Iraqi national',
    sij: 'Special immigrant juvenile',
    'si-g4-nato6': 'Special immigrant — retired G-4 or NATO-6 employee',
    'si-broadcaster': 'Special immigrant — international broadcaster',
    refugee: 'Refugee (admitted under section 207)',
    asylee: 'Asylee (granted asylum under section 208)',
    't-nonimmigrant': 'T nonimmigrant (human trafficking victim)',
    'u-nonimmigrant': 'U nonimmigrant (crime victim)',
    hrifa: 'Haitian Refugee Immigration Fairness Act',
    lautenberg: 'Lautenberg Parolee',
    'diplomat-section-13': 'Section 13 diplomat',
    'hrifa-battered': 'HRIFA — battered spouse or child',
    indochinese: 'Indochinese — Vietnam, Cambodia, or Laos national',
    cuban: 'Cuban Adjustment Act',
    'cuban-battered': 'Cuban Adjustment Act — battered spouse or child',
    registry: 'Registry — continuous residence since before 1972',
    'born-diplomatic': 'Born in U.S. to a foreign diplomat (rare)',
    'diversity-visa': 'Diversity Visa (DV lottery)',
    other: 'Other — explain below',
  },
};

/**
 * Title-cases a single enum key (snake_case / kebab-case → Title
 * Case). Numeric prefixes are preserved verbatim (`5_pct_owner` →
 * `5 Pct Owner`).
 */
function titleCaseEnumKey(key: string): string {
  return key
    .split(/[_\-]/)
    .map(part => {
      if (part.length === 0) return part;
      const first = part[0].toUpperCase();
      return first + part.slice(1);
    })
    .join(' ');
}

/**
 * Internal: collects every enum key referenced by any of the field's
 * `pdfConsumers[].widgetValueProjection` maps, deduplicated and in
 * insertion order. Returns an empty list if no consumer carries a
 * projection (the dispatcher then renders an empty option list and
 * relies on the discovery audit to surface the gap).
 */
function collectEnumKeys(field: FieldDefinition): readonly string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const consumer of field.pdfConsumers) {
    const projection = consumer.widgetValueProjection;
    if (projection === undefined) continue;
    for (const key of Object.keys(projection)) {
      if (seen.has(key)) continue;
      seen.add(key);
      ordered.push(key);
    }
  }
  return ordered;
}

/**
 * Module-level discovery store. Populated lazily as
 * `resolveFieldOptions` runs across the catalog. Slice 8 reads this
 * and emits a list of unlabeled keys to a developer-facing console
 * warning + a `docs/rebuild/phase-7a-discoveries.md` artifact.
 */
const fallbackDiscoveries = new Map<string, Set<string>>();

/**
 * Module-level discovery store for select fields that have neither a
 * curated override nor a `widgetValueProjection` to derive enum keys
 * from. Surfaces empty option lists to Slice 8's audit.
 */
const noEnumDomainDiscoveries = new Set<string>();

/**
 * Returns the human-readable option list for a select / multi_select
 * field. Resolution order:
 *
 *   1. If the field has a curated override map
 *      (`OPTION_LABEL_OVERRIDES[field.id]`), the override map's keys
 *      define the enum domain and the override map's values define
 *      the labels. Catalog projection keys NOT in the override map
 *      are appended after the curated keys (so the catalog can grow
 *      enum values without a curator gate).
 *   2. Otherwise the catalog's `widgetValueProjection` keys define
 *      the enum domain and the labels are title-case-fallback.
 *
 * Non-select fields return an empty list. Select fields with neither
 * a curated override nor a projection return an empty list and are
 * tracked in `getDiscoveredNoEnumDomain()` so Slice 8 can surface
 * them.
 */
/**
 * Phase 7i — module-level memoization of resolved option lists.
 *
 * The resolver is a pure function of the field (and the curated
 * override map, which is module-static). Catalog fields are
 * referentially stable across the app's lifetime, so we key by
 * `field.id` and reuse the same array for every subsequent call.
 *
 * Before this cache, every render of every SelectInput rebuilt the
 * option list — for COUNTRIES that's a fresh ~250-entry array per
 * render, per select field, which churns the GC during typing. The
 * cache returns the same array reference for the lifetime of the
 * process, which also lets SelectInput's `React.memo` (where used)
 * skip re-renders by props equality.
 *
 * The discovery side-effect is preserved: when a field falls through
 * to title-case fallback or hits the empty-domain branch, we still
 * record it on the FIRST resolution. Subsequent calls hit the cache
 * and skip the side-effect — that's correct because the discovery is
 * idempotent (a Set of (fieldId, key) pairs).
 */
const optionsCache = new Map<string, readonly IResolvedOption[]>();

/** Test seam — clears the memoization cache. */
export function __resetOptionsCacheForTests(): void {
  optionsCache.clear();
}

/**
 * U.S. states options list — exposed for callers that want to override
 * a free-text `region` / `province` sub-field to a state dropdown when
 * the cluster's `country` is "US". Several catalog "place" bags (e.g.,
 * `beneficiary.current_spouse.marriage.place`) use a generic `region`
 * text field instead of a country-specific `state` select; this helper
 * lets the address composite swap in the US states list at render time.
 */
let _usStatesOptionsMemo: readonly IResolvedOption[] | null = null;
export function getUsStateOptions(): readonly IResolvedOption[] {
  if (_usStatesOptionsMemo === null) {
    _usStatesOptionsMemo = Object.entries(US_STATES).map(([value, label]) => ({
      value,
      label,
    }));
  }
  return _usStatesOptionsMemo;
}

export function resolveFieldOptions(
  field: FieldDefinition,
): readonly IResolvedOption[] {
  if (field.valueType !== 'select' && field.valueType !== 'multi_select') {
    return EMPTY_OPTIONS;
  }
  const cached = optionsCache.get(field.id);
  if (cached !== undefined) return cached;

  const overrides = OPTION_LABEL_OVERRIDES[field.id];
  const projectionKeys = collectEnumKeys(field);

  if (overrides === undefined && projectionKeys.length === 0) {
    noEnumDomainDiscoveries.add(field.id);
    optionsCache.set(field.id, EMPTY_OPTIONS);
    return EMPTY_OPTIONS;
  }

  const result: IResolvedOption[] = [];
  const seen = new Set<string>();

  // Curated keys first (in object-property order — preserves the
  // curator's preferred ordering).
  if (overrides !== undefined) {
    for (const [key, label] of Object.entries(overrides)) {
      if (seen.has(key)) continue;
      seen.add(key);
      result.push({ value: key, label });
    }
  }

  // Append any catalog-projection keys not already in the curated set,
  // using the title-case fallback. Each fallback adds a discovery
  // entry so the curator audit can pick it up.
  for (const key of projectionKeys) {
    if (seen.has(key)) continue;
    seen.add(key);
    result.push({ value: key, label: titleCaseEnumKey(key) });
    const set = fallbackDiscoveries.get(field.id) ?? new Set<string>();
    set.add(key);
    fallbackDiscoveries.set(field.id, set);
  }

  optionsCache.set(field.id, result);
  return result;
}

/**
 * Shared empty-options sentinel — returned for every non-select field
 * and for select fields with neither curated overrides nor a projection
 * domain. Sharing one frozen reference keeps `===` equality stable so
 * downstream `React.memo` checks short-circuit cleanly.
 */
const EMPTY_OPTIONS: readonly IResolvedOption[] = Object.freeze([]);

/**
 * Returns the set of select-field ids that have neither a curated
 * override nor a `widgetValueProjection` — Slice 8's discovery audit
 * surfaces these as the highest-priority gaps.
 */
export function getDiscoveredNoEnumDomain(): ReadonlySet<FieldId> {
  return noEnumDomainDiscoveries as unknown as ReadonlySet<FieldId>;
}

/**
 * Returns a snapshot of every (fieldId, key) pair that fell back to
 * the title-cased label rather than a curated override. Used by
 * 's discovery emit. Resets are intentionally not exposed —
 * the discovery is an additive log across the lifetime of the
 * process.
 */
export function getDiscoveredFallbacks(): ReadonlyMap<
  FieldId,
  ReadonlySet<string>
> {
  // Cast through unknown — `Map<string, Set>` and `Map<FieldId, Set>`
  // share runtime shape; FieldId is a branded string at compile time.
  return fallbackDiscoveries as unknown as ReadonlyMap<
    FieldId,
    ReadonlySet<string>
  >;
}
